import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Minus,
  Trash2,
  Search,
  ShoppingCart,
  Clock,
  X,
  Coffee,
  Grid3X3,
  List,
  ChefHat,
  Users,
  CheckCircle2,
  WifiOff,
  Printer
} from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useGarcomAuth } from '../contexts/GarcomAuthContext';
import { useModal } from '../hooks/useScrollLock';
import { useBodyLock } from '../hooks/useBodyLock';
import { normalizeString, formatarMoeda } from '../utils/stringUtils';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import ProductModal from '../components/ProductModal';
import ComandaBottomBar from '../components/ComandaBottomBar';
import SearchNavigation from '../components/SearchNavigation';
import ProductCard from '../components/ProductCard';
import ProductCardCompact from '../components/ProductCardCompact';
import VirtualizedProductList from '../components/VirtualizedProductList';
import NotificationBadge from '../components/NotificationBadge';
import NetworkStatusBanner from '../components/NetworkStatusBanner';
import { cacheService } from '../services/cacheService';
import api from '../services/api';
import { config } from '../config/environment';
import toast from 'react-hot-toast';

interface Produto {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  imagem_url?: string;
  categoria_nome?: string;
  categoria_id?: number;
  ativo?: boolean;
  tempo_preparo: number;
  codigo?: string;
  codigo_barras?: string;
  visivel_garcom?: boolean;
  visivel_delivery?: boolean;
  visivel_pdv?: boolean;
  perguntar_imprimir?: boolean;
  venda_por_peso?: boolean;
  opcoes_personalizacao?: OpcaoPersonalizacao[];
}

interface OpcaoPersonalizacao {
  id: number;
  nome: string;
  tipo: 'adicionar' | 'remover';
  obrigatorio: boolean;
  max_selecoes: number;
  itens: ItemOpcao[];
}

interface ItemOpcao {
  id: number;
  nome: string;
  preco_adicional: number;
  quantidade: number;
}

interface MobileProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  produto: Produto | null;
  quantidade: number;
  observacoes: string;
  onQuantidadeChange: (qtd: number) => void;
  onObservacoesChange: (obs: string) => void;
  onAdicionar: () => void;
  onCancelar: () => void;
}

interface ItemComanda {
  id: number;
  produto_id: number;
  produto_nome: string;
  quantidade: number;
  peso?: number | null;
  preco_unitario: number;
  observacoes: string;
  personalizacoes: any[];
  enviado_cozinha: boolean;
  garcom_id?: number | null;
  garcom_nome?: string | null;
  pendente_sincronizacao?: boolean;
  created_at?: string;
  precisa_cozinha?: boolean | number;
}

interface Comanda {
  id: number;
  mesa_id: number | null;
  mesa_numero: string | null;
  mesa_nome: string | null;
  comanda_numero: string;
  comanda_nome: string;
  garcom_id: number | string;
  status: string;
  total: number;
  data_abertura: string;
  itens: ItemComanda[];
}

interface Categoria {
  id: number;
  nome: string;
  visivel_garcom?: boolean;
  visivel_pdv?: boolean;
  visivel_delivery?: boolean;
}

interface Mesa {
  id: number;
  numero: string;
  nome: string;
  capacidade: number;
  status: 'livre' | 'ocupada' | 'reservada' | 'manutencao';
}

// Helper local removido em favor do utilitário centralizado

const GarcomComandaPage: React.FC = () => {
  const { comandaId } = useParams<{ comandaId: string }>();
  const navigate = useNavigate();
  const { garcom } = useGarcomAuth();
  const { isMobile, isTablet, isDesktop, deviceType, hapticFeedback } = useMobileOptimizations();

  const [comanda, setComanda] = useState<Comanda | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'cardapio' | 'carrinho'>('cardapio');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState<number | ''>('');
  const [visualizacao, setVisualizacao] = useState<'grade' | 'lista'>('grade');
  const [showProdutoModal, setShowProdutoModal] = useState(false);
  const [produtoDestaqueIndex, setProdutoDestaqueIndex] = useState<number>(0);
  const [produtoSelecionado, setProdutoSelecionado] = useState<Produto | null>(null);
  const [quantidade, setQuantidade] = useState(1);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [peso, setPeso] = useState<number | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [personalizacoes, setPersonalizacoes] = useState<any[]>([]);
  const [showModalCozinha, setShowModalCozinha] = useState(false);
  const [itensCozinhaIds, setItensCozinhaIds] = useState<number[]>([]);
  const [itensCozinhaDetalhes, setItensCozinhaDetalhes] = useState<ItemComanda[]>([]);
  const [msgCozinhaAux, setMsgCozinhaAux] = useState('');
  const [showDefinirCodigoModal, setShowDefinirCodigoModal] = useState(false);
  const [showReenviarModal, setShowReenviarModal] = useState(false);
  const [itensReenviarIds, setItensReenviarIds] = useState<number[]>([]);
  const [reenviando, setReenviando] = useState(false);
  const [codigoParaDefinir, setCodigoParaDefinir] = useState('');
  const [produtoPendenteCodigo, setProdutoPendenteCodigo] = useState<Produto | null>(null);
  const [dadosPendenteAdicao, setDadosPendenteAdicao] = useState<{
    quantidade: number;
    peso: number | null;
    observacoes: string;
    personalizacoes: any[];
  } | null>(null);
  const [hasPushState, setHasPushState] = useState(false);
  const [isOffline, setIsOffline] = useState(() => {
    if (typeof window !== 'undefined') {
      return !navigator.onLine;
    }
    return false;
  });
  const wasOfflineRef = useRef(false);

  const modoCompacto = isMobile;
  
  useBodyLock(showProdutoModal);

  const { isRefreshing, pullProgress } = usePullToRefresh({
    onRefresh: async () => {
      await carregarDados();
      hapticFeedback('light');
    },
    enabled: isMobile
  });

  const produtosCacheRef = useRef<Produto[]>([]);
  const lastComandaHashRef = useRef<string>('');
  const UPDATE_INTERVAL = 10000;

  const createComandaHash = (comanda: Comanda | null): string => {
    if (!comanda) return '';
    return JSON.stringify({
      id: comanda.id,
      total: comanda.total,
      status: comanda.status,
      itens_count: comanda.itens?.length || 0,
      itens_ids: comanda.itens?.map(i => ({ id: i.id, quantidade: i.quantidade, enviado_cozinha: i.enviado_cozinha })) || []
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setBuscaDebounced(busca);
    }, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);

      const isOnline = cacheService.isOnline();
      let produtosData = null;
      let categoriasData = null;

      if (!isOnline) {
        produtosData = await cacheService.getProducts();
        categoriasData = await cacheService.getCategories();

        if (produtosData) {
          const produtosAtivos = produtosData.filter((p: Produto) => p.ativo);
          setProdutos(produtosAtivos);
          produtosCacheRef.current = produtosAtivos;
        }
        if (categoriasData) {
          setCategorias(categoriasData);
        }

        try {
          const comandaResponse = await api.get(`/api/garcom/comandas-mesa/${comandaId}`);
          setComanda(comandaResponse.data);
          lastComandaHashRef.current = createComandaHash(comandaResponse.data);
        } catch {
          console.log('⚠️ Não foi possível carregar comanda offline');
        }

        setLoading(false);
        return;
      }

      const [comandaResult, fastProdutosResult, categoriasResult] = await Promise.allSettled([
        api.get(`/api/garcom/comandas-mesa/${comandaId}`),
        api.get('/api/produtos?limit=20&offset=0'),
        api.get('/api/categorias')
      ]);

      if (comandaResult.status === 'fulfilled') {
        const comandaData = comandaResult.value.data;
        setComanda(comandaData);
        lastComandaHashRef.current = createComandaHash(comandaData);
      } else {
        throw comandaResult.reason;
      }

      if (fastProdutosResult.status === 'fulfilled') {
        const produtosIniciais = (fastProdutosResult.value.data.data || fastProdutosResult.value.data).filter((p: Produto) => p.ativo && p.visivel_garcom);
        setProdutos(produtosIniciais);
        produtosCacheRef.current = produtosIniciais;
      }

      if (categoriasResult.status === 'fulfilled') {
        const categoriasAtivas = categoriasResult.value.data.filter((c: Categoria) => c.visivel_garcom);
        setCategorias(categoriasAtivas);
        await cacheService.saveCategories(categoriasResult.value.data);
      }

      setIsOffline(false);

      api.get('/api/produtos').then(async (fullResponse) => {
        const todosProdutos = fullResponse.data.data || fullResponse.data;
        const todosAtivos = todosProdutos.filter((p: Produto) => p.ativo && p.visivel_garcom);
        
        setProdutos(todosAtivos);
        produtosCacheRef.current = todosAtivos;
        await cacheService.saveProducts(todosProdutos);
      }).catch(err => console.error('⚠️ Falha no sync background', err));

    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';

      if (isNetworkError) {
        setIsOffline(true);
        const produtosCache = await cacheService.getProducts();
        const categoriasCache = await cacheService.getCategories();

        if (produtosCache) {
          const produtosAtivos = produtosCache.filter((p: Produto) => p.ativo);
          setProdutos(produtosAtivos);
          produtosCacheRef.current = produtosAtivos;
        }
        if (categoriasCache) {
          setCategorias(categoriasCache);
        }

        toast('Conexão instável. Usando modo de segurança offline.', {
          icon: '📡',
          style: {
            background: '#d97706',
            color: '#fff',
          },
          id: 'vps-offline-warning-comanda'
        });
      } else {
        if (cacheService.isOnline()) {
          toast.error('Erro ao carregar dados. Usando cache...');
        } else {
          toast.error('Erro ao carregar dados');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [comandaId]);

  const { isSyncing } = useOfflineSync(() => {
    carregarDados();
  });

  useEffect(() => {
    if (comandaId) {
      carregarDados();
      const interval = setInterval(() => {
        api.get(`/api/garcom/comandas-mesa/${comandaId}`)
          .then(response => {
            setIsOffline(false);
            const newComanda = response.data;
            const newHash = createComandaHash(newComanda);

            if (newHash !== lastComandaHashRef.current) {
              const oldTotal = Number(comanda?.total || 0);
              const newTotal = Number(newComanda.total || 0);

              setComanda(newComanda);
              lastComandaHashRef.current = newHash;

              if (oldTotal > 0 && newTotal !== oldTotal) {
                hapticFeedback('light');
                if (Math.abs(newTotal - oldTotal) > 0.01) {
                  toast.success('Comanda atualizada!', { duration: 2000 });
                }
              }
            }
          })
          .catch(error => {
            console.error('Erro ao atualizar comanda:', error);
            const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';
            if (isNetworkError) {
              setIsOffline(true);
            }
          });
      }, UPDATE_INTERVAL);

      return () => clearInterval(interval);
    }
  }, [comandaId, carregarDados]);

  const produtosFiltrados = useMemo(() => {
    const filtrados = produtos.filter(produto => {
      const buscaOriginal = buscaDebounced.trim();
      const buscaNorm = normalizeString(buscaOriginal);
      const matchCategoria = categoriaFiltro === '' || produto.categoria_id === categoriaFiltro;

      if (!buscaOriginal) {
        return matchCategoria;
      }

      const isNumeric = /^\d+$/.test(buscaOriginal);

      if (isNumeric) {
        const matchCodigo = produto.codigo && produto.codigo.trim() === buscaOriginal;
        const matchBarras = produto.codigo_barras && produto.codigo_barras.trim() === buscaOriginal;
        
        return (matchCodigo || matchBarras) && matchCategoria;
      }

      const matchNome = normalizeString(produto.nome).includes(buscaNorm);

      return matchNome && matchCategoria;
    });

    if (produtoDestaqueIndex >= filtrados.length) {
      setProdutoDestaqueIndex(0);
    }

    return filtrados;
  }, [produtos, buscaDebounced, categoriaFiltro, produtoDestaqueIndex]);

  const produtoDestaque = produtosFiltrados[produtoDestaqueIndex] || null;

  const navegarBusca = useCallback((direction: 'up' | 'down') => {
    if (produtosFiltrados.length === 0) return;

    if (direction === 'up') {
      setProdutoDestaqueIndex(prev =>
        prev > 0 ? prev - 1 : produtosFiltrados.length - 1
      );
    } else {
      setProdutoDestaqueIndex(prev =>
        prev < produtosFiltrados.length - 1 ? prev + 1 : 0
      );
    }

    hapticFeedback('light');
  }, [produtosFiltrados.length, hapticFeedback]);

  useEffect(() => {
    setProdutoDestaqueIndex(0);
  }, [buscaDebounced]);

  const executarAdicaoItemComanda = useCallback(async (prod: Produto, quantidadeVal: number, pesoVal: number | null, observacoesVal: string, personalizacoesVal: any[]) => {
    if (!comanda) return;
    try {
      let enviarImpressora: boolean | undefined = undefined;

      if (prod.perguntar_imprimir) {
        enviarImpressora = window.confirm(`Deseja imprimir este item (${prod.nome}) na cozinha/copa?`);
      }

      const itemData = {
        produto_id: prod.id,
        quantidade: quantidadeVal,
        peso: pesoVal,
        observacoes: observacoesVal,
        personalizacoes: personalizacoesVal,
        enviar_impressora: enviarImpressora
      };

      if (!navigator.onLine) {
        await cacheService.addToSyncQueue('POST', `/api/garcom/comandas-mesa/${comandaId}/itens`, itemData);
        
        const fakeItem: ItemComanda = {
          id: -Date.now(),
          produto_id: prod.id,
          produto_nome: prod.nome,
          quantidade: quantidadeVal,
          peso: pesoVal,
          preco_unitario: prod.preco,
          observacoes: observacoesVal,
          personalizacoes: personalizacoesVal,
          enviado_cozinha: false,
          pendente_sincronizacao: true
        };
        
        const novoTotal = Number(comanda.total || 0) + (fakeItem.peso && fakeItem.peso > 0 ? (fakeItem.preco_unitario * fakeItem.peso) : (fakeItem.preco_unitario * fakeItem.quantidade));
        
        setComanda({
          ...comanda,
          id: comanda.id || Number(comandaId),
          mesa_id: comanda.mesa_id || null,
          total: novoTotal,
          itens: [...(comanda.itens || []), fakeItem],
          status: comanda.status || 'ativa'
        } as Comanda);

        hapticFeedback('medium');
        toast.success('Produto salvo localmente (Aguardando Rede)', { icon: '📡' });
      } else {
        await api.post(`/api/garcom/comandas-mesa/${comandaId}/itens`, itemData);
        hapticFeedback('medium');
        toast.success('Produto adicionado à comanda!');
        await carregarDados();
      }
    } catch (error: any) {
      if (!navigator.onLine || error.message === 'Network Error') {
        toast.error('Você está offline. O produto foi salvo e será sincronizado.');
      } else {
        toast.error(error.response?.data?.erro || 'Erro ao adicionar produto');
      }
    }
  }, [comanda, comandaId, hapticFeedback, carregarDados]);

  const confirmarDefinicaoCodigo = useCallback(async () => {
    if (!produtoPendenteCodigo || !dadosPendenteAdicao) return;

    const codigoVal = codigoParaDefinir.trim();
    if (!codigoVal) {
      toast.error('Informe um código válido ou clique em Pular');
      return;
    }

    try {
      if (navigator.onLine) {
        await api.put(`/api/garcom/produtos/${produtoPendenteCodigo.id}/codigo-rapido`, { codigo: codigoVal });
        toast.success(`Código rápido "${codigoVal}" cadastrado para ${produtoPendenteCodigo.nome}!`);
      }

      setProdutos(prev => prev.map(p => p.id === produtoPendenteCodigo.id ? { ...p, codigo: codigoVal } : p));
      
      await executarAdicaoItemComanda(
        produtoPendenteCodigo,
        dadosPendenteAdicao.quantidade,
        dadosPendenteAdicao.peso,
        dadosPendenteAdicao.observacoes,
        dadosPendenteAdicao.personalizacoes
      );
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao cadastrar código rápido');
      await executarAdicaoItemComanda(
        produtoPendenteCodigo,
        dadosPendenteAdicao.quantidade,
        dadosPendenteAdicao.peso,
        dadosPendenteAdicao.observacoes,
        dadosPendenteAdicao.personalizacoes
      );
    } finally {
      setShowDefinirCodigoModal(false);
      setProdutoPendenteCodigo(null);
      setDadosPendenteAdicao(null);
      setCodigoParaDefinir('');
    }
  }, [produtoPendenteCodigo, dadosPendenteAdicao, codigoParaDefinir, executarAdicaoItemComanda]);

  const pularDefinicaoCodigo = useCallback(async () => {
    if (!produtoPendenteCodigo || !dadosPendenteAdicao) return;

    await executarAdicaoItemComanda(
      produtoPendenteCodigo,
      dadosPendenteAdicao.quantidade,
      dadosPendenteAdicao.peso,
      dadosPendenteAdicao.observacoes,
      dadosPendenteAdicao.personalizacoes
    );

    setShowDefinirCodigoModal(false);
    setProdutoPendenteCodigo(null);
    setDadosPendenteAdicao(null);
    setCodigoParaDefinir('');
  }, [produtoPendenteCodigo, dadosPendenteAdicao, executarAdicaoItemComanda]);

  const adicionarProduto = useCallback(async () => {
    if (!produtoSelecionado || !comanda) return;

    if (!produtoSelecionado.codigo || produtoSelecionado.codigo.trim() === '') {
      setProdutoPendenteCodigo(produtoSelecionado);
      setDadosPendenteAdicao({
        quantidade,
        peso,
        observacoes,
        personalizacoes
      });
      setShowProdutoModal(false);
      setShowDefinirCodigoModal(true);
      return;
    }

    await executarAdicaoItemComanda(produtoSelecionado, quantidade, peso, observacoes, personalizacoes);

    setShowProdutoModal(false);
    setProdutoSelecionado(null);
    setQuantidade(1);
    setPeso(null);
    setObservacoes('');
    setPersonalizacoes([]);
  }, [produtoSelecionado, comanda, quantidade, peso, observacoes, personalizacoes, executarAdicaoItemComanda]);

  const removerItem = useCallback(async (itemId: number) => {
    if (!confirm('Tem certeza que deseja remover este item?')) return;

    try {
      await api.delete(`/api/garcom/comandas-mesa/${comandaId}/itens/${itemId}`);
      hapticFeedback('light');
      toast.success('Item removido da comanda!');
      const comandaResponse = await api.get(`/api/garcom/comandas-mesa/${comandaId}`);
      setComanda(comandaResponse.data);
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao remover item');
    }
  }, [comandaId, hapticFeedback]);

  const atualizarQuantidade = useCallback(async (itemId: number, novaQuantidade: number) => {
    if (novaQuantidade < 1) return;

    try {
      await api.put(`/api/garcom/comandas-mesa/${comandaId}/itens/${itemId}`, {
        quantidade: novaQuantidade
      });
      const comandaResponse = await api.get(`/api/garcom/comandas-mesa/${comandaId}`);
      setComanda(comandaResponse.data);
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao atualizar quantidade');
    }
  }, [comandaId]);

  const enviarParaCozinha = async () => {
    if (!comanda || !comanda.itens || comanda.itens.length === 0) {
      toast.error('Adicione itens à comanda antes de enviar para a cozinha');
      return;
    }

    const itensParaCozinha = comanda.itens.filter((item: ItemComanda) => {
      return !item.enviado_cozinha;
    });

    if (itensParaCozinha.length === 0) {
      toast.error('Não há itens (produtos) que precisam ser enviados para a cozinha');
      return;
    }

    setItensCozinhaIds(itensParaCozinha.map((i: ItemComanda) => i.id));
    setItensCozinhaDetalhes(itensParaCozinha);
    setMsgCozinhaAux('Ao confirmar, os pedidos que necessitam de preparo serão impressos na cozinha automaticamente.');
    setShowModalCozinha(true);
  };

  const reenviarParaCozinha = async () => {
    if (itensReenviarIds.length === 0) {
      toast.error('Selecione pelo menos um item para reenviar à cozinha');
      return;
    }

    try {
      setReenviando(true);
      const response = await api.post(`/api/garcom/comandas/${comandaId}/reenviar-cozinha`, {
        itensIds: itensReenviarIds
      });

      hapticFeedback('heavy');
      toast.success(response.data.mensagem || 'Itens reenviados para cozinha!');
      setShowReenviarModal(false);
      setItensReenviarIds([]);
      carregarDados();
    } catch (error: any) {
      console.error('Erro ao reenviar itens para a cozinha:', error);
      toast.error(error.response?.data?.erro || 'Erro ao reenviar itens');
    } finally {
      setReenviando(false);
    }
  };

  const confirmarEnvioCozinha = async (imprimir: boolean) => {
    try {
      setShowModalCozinha(false);
      const response = await api.post(`/api/garcom/comandas/${comandaId}/enviar-cozinha`, {
        apenas_ids: itensCozinhaIds,
        imprimir: imprimir
      });

      hapticFeedback('heavy');
      toast.success(response.data.mensagem || 'Pedido enviado para a cozinha!');
    } catch (error: any) {
      console.error('Erro ao enviar para cozinha:', error);
      toast.error(error.response?.data?.erro || 'Erro ao enviar pedido para a cozinha');
    } finally {
      carregarDados();
    }
  };

  const abrirProdutoModal = useCallback((produto: Produto) => {
    setProdutoSelecionado(produto);
    setQuantidade(1);
    setObservacoes('');
    setPersonalizacoes([]);
    setPeso(null);
    setShowProdutoModal(true);
  }, []);

  const confirmarPeso = (pesoNum: number) => {
    setPeso(pesoNum);
    setShowWeightModal(false);
    setShowProdutoModal(true);
  };

  const handleSwipeLeft = useCallback(async (produto: Produto) => {
    if (isMobile && !showProdutoModal && comanda) {
      if (produto.venda_por_peso) {
        abrirProdutoModal(produto);
        return;
      }

      if (!produto.codigo || produto.codigo.trim() === '') {
        setProdutoPendenteCodigo(produto);
        setDadosPendenteAdicao({
          quantidade: 1,
          peso: null,
          observacoes: '',
          personalizacoes: []
        });
        setShowDefinirCodigoModal(true);
        return;
      }

      await executarAdicaoItemComanda(produto, 1, null, '', []);
    }
  }, [isMobile, showProdutoModal, comanda, comandaId, hapticFeedback, abrirProdutoModal, executarAdicaoItemComanda]);

  const handleSwipeRight = useCallback((produto: Produto) => {
    if (isMobile && !showProdutoModal) {
      abrirProdutoModal(produto);
      hapticFeedback('light');
    }
  }, [isMobile, showProdutoModal, abrirProdutoModal, hapticFeedback]);

  const itensPendentesCozinha = useMemo(() => {
    if (!comanda || !comanda.itens) return 0;
    
    let count = 0;
    comanda.itens.forEach((item: ItemComanda) => {
      if (!item.enviado_cozinha) count++;
    });
    return count;
  }, [comanda]);

  const handleVoltar = useCallback(() => {
    if (itensPendentesCozinha > 0) {
      const confirmar = window.confirm('Existem itens pendentes que não foram enviados para a cozinha. Deseja sair assim mesmo?');
      if (!confirmar) return;
    }
    navigate('/garcom/home');
  }, [itensPendentesCozinha, navigate]);

  // Intercepta fechar a aba/recarregar com itens pendentes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (itensPendentesCozinha > 0) {
        e.preventDefault();
        e.returnValue = 'Existem itens pendentes nesta comanda. Deseja realmente sair?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [itensPendentesCozinha]);

  // Gerencia a navegação de voltar com confirmação se houver itens pendentes
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (itensPendentesCozinha > 0) {
        const confirmar = window.confirm('Existem itens pendentes nesta comanda. Deseja realmente voltar ao início?');
        if (!confirmar) {
          e.stopImmediatePropagation();
          window.history.pushState({ block: true }, '', window.location.pathname);
        }
      }
    };

    window.addEventListener('popstate', handlePopState, true);
    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [itensPendentesCozinha]);

  const itensComandaFiltrados = useMemo(() => {
    if (!comanda || !comanda.itens) return [];
    if (!buscaDebounced.trim()) return comanda.itens;

    const buscaNorm = normalizeString(buscaDebounced);
    return comanda.itens.filter(item => 
      normalizeString(item.produto_nome).includes(buscaNorm)
    );
  }, [comanda, buscaDebounced]);


  useEffect(() => {
    if (wasOfflineRef.current && !isOffline) {
      carregarDados();
      toast.success('Conexão restabelecida. Dados atualizados!', { id: 'connection-restored-comanda' });
    }
    wasOfflineRef.current = isOffline;
  }, [isOffline, carregarDados]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando comanda...</p>
          {isOffline && (
            <p className="text-yellow-400 text-sm mt-2">📴 Modo offline - usando cache</p>
          )}
        </div>
      </div>
    );
  }

  if (!comanda) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
        <div className="text-center bg-gray-800/80 border border-gray-700 p-6 rounded-2xl max-w-sm w-full shadow-xl">
          <h1 className="text-xl font-bold text-white mb-2">Comanda não encontrada</h1>
          <p className="text-gray-400 text-sm mb-6">Não foi possível carregar as informações desta comanda.</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => carregarDados()}
              className="w-full bg-orange-500 text-white py-3 rounded-xl font-bold hover:bg-orange-600 transition-all active:scale-95 shadow-lg shadow-orange-500/20"
            >
              Tentar Novamente
            </button>
            <button
              onClick={() => navigate('/garcom/home')}
              className="w-full bg-gray-700 text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
            >
              Voltar ao Início
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <NetworkStatusBanner onReconnect={carregarDados} />

      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={handleVoltar}
              className="p-2 bg-gray-700/80 rounded-xl hover:bg-gray-600 transition-colors touch-manipulation active:scale-95 flex-shrink-0"
              title="Voltar"
            >
              <ArrowLeft className="h-5 w-5 text-white" />
            </button>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-lg font-bold text-white truncate leading-tight">
                {comanda.mesa_id ? comanda.mesa_nome : 'Avulsa'} — {comanda.comanda_nome}
              </h1>
              <p className="text-xs text-gray-400">#{comanda.comanda_numero}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {comanda.itens && comanda.itens.length > 0 && (
              <button
                onClick={() => {
                  hapticFeedback('light');
                  setItensReenviarIds([]);
                  setShowReenviarModal(true);
                }}
                className="p-2 bg-gray-700/80 hover:bg-gray-600 rounded-xl transition-all touch-manipulation active:scale-95 flex-shrink-0 flex items-center justify-center"
                title="Reenviar Itens para Cozinha (Reimpressão)"
              >
                <Printer className="h-5 w-5 text-white" />
              </button>
            )}
            <div className="text-right">
              <p className="text-lg sm:text-xl font-bold text-orange-400">
                {formatarMoeda(comanda.total)}
              </p>
              <p className="text-[10px] text-gray-500">total</p>
            </div>
            {!isMobile && comanda.itens && comanda.itens.some(item => !item.enviado_cozinha) && (
              <button
                onClick={enviarParaCozinha}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2.5 rounded-xl font-semibold flex items-center gap-2 transition-all hover:shadow-lg active:scale-95 text-sm relative"
              >
                <ChefHat className="h-4 w-4" />
                Enviar Cozinha
                <NotificationBadge count={itensPendentesCozinha} />
              </button>
            )}
          </div>
        </div>

        {isMobile && isRefreshing && (
          <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4">
            <div className="bg-gray-800 rounded-full px-4 py-2 shadow-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
            </div>
          </div>
        )}

        <div className={`flex flex-col lg:grid lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 ${isMobile ? 'pb-28' : ''}`}>
          <div className={`order-1 lg:order-2 lg:col-span-1 ${isMobile && activeTab !== 'carrinho' ? 'hidden' : 'block'}`}>
            <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-600/30 lg:sticky lg:top-20">
              <h2 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6" id="itens-comanda-heading">Itens da Comanda</h2>

              {itensComandaFiltrados.length > 0 ? (
                <div className={`space-y-2 sm:space-y-4`} role="list" aria-labelledby="itens-comanda-heading">
                  {itensComandaFiltrados.map((item) => (
                    <div key={item.id} role="listitem" className={`rounded-lg sm:rounded-xl p-3 sm:p-5 border shadow-md bg-gray-700 border-gray-600 shadow-none ${item.enviado_cozinha ? 'opacity-80' : ''}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <h3 className="font-medium text-sm truncate text-white">
                            {item.produto_nome}
                          </h3>
                          {item.enviado_cozinha ? (
                            <div className="flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-bold text-white bg-green-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow shadow-green-500/20">
                              <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" /> Enviado
                            </div>
                          ) : item.pendente_sincronizacao ? (
                            <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full flex-shrink-0 flex items-center gap-1">
                              <WifiOff className="h-3 w-3" /> Pendente de Sync
                            </span>
                          ) : (
                            <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full flex-shrink-0">
                              Pendente
                            </span>
                          )}
                        </div>
                        {!item.enviado_cozinha && (
                          <button
                            onClick={() => removerItem(item.id)}
                            className="text-red-400 hover:text-red-300 touch-manipulation active:scale-95 flex-shrink-0 ml-2"
                            aria-label={`Remover ${item.produto_nome} da comanda`}
                          >
                            <Trash2 className={`${isMobile ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
                          </button>
                        )}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {!item.enviado_cozinha && (!item.peso || item.peso <= 0) && (
                            <button
                              onClick={() => atualizarQuantidade(item.id, Math.max(1, item.quantidade - 1))}
                              className={`${isMobile ? 'w-7 h-7' : 'w-6 h-6 sm:w-7 sm:h-7'} bg-gray-600 rounded-full flex items-center justify-center text-white hover:bg-gray-500 touch-manipulation active:scale-95`}
                              aria-label={`Diminuir quantidade de ${item.produto_nome}`}
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                          )}
                          <span className={`${(!item.peso || item.peso <= 0) ? 'mx-1' : ''} text-white font-medium text-sm min-w-[24px] text-center`} aria-label={`Quantidade: ${item.quantidade}`}>
                            {item.peso && item.peso > 0 
                              ? `${Number(item.quantidade)} un x ${parseFloat(String(item.peso)).toFixed(3)} KG` 
                              : Number(item.quantidade)}
                          </span>
                          {!item.enviado_cozinha && (!item.peso || item.peso <= 0) && (
                            <button
                              onClick={() => atualizarQuantidade(item.id, item.quantidade + 1)}
                              className={`${isMobile ? 'w-7 h-7' : 'w-6 h-6 sm:w-7 sm:h-7'} bg-gray-600 rounded-full flex items-center justify-center text-white hover:bg-gray-500 touch-manipulation active:scale-95`}
                              aria-label={`Aumentar quantidade de ${item.produto_nome}`}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          )}
                        </div>

                        <span className="text-orange-400 font-bold text-sm">
                          {formatarMoeda(item.peso && item.peso > 0 
                            ? item.preco_unitario * item.peso 
                            : item.preco_unitario * item.quantidade)}
                        </span>
                      </div>

                      {item.observacoes && (
                        <p className="text-gray-400 text-xs sm:text-sm mt-2">
                          Obs: {item.observacoes}
                        </p>
                      )}

                      {item.garcom_nome && (
                        <p className="text-orange-400/80 text-[10px] uppercase font-bold mt-1.5 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          Adicionado por: {item.garcom_nome}
                        </p>
                      )}

                      {item.created_at && (
                        <p className="text-gray-400 text-[10px] mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(item.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8" role="status" aria-live="polite">
                  <ShoppingCart className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" aria-hidden="true" />
                  <p className="text-gray-400 text-sm sm:text-base">Nenhum item na comanda</p>
                </div>
              )}
            </div>
          </div>

          <div className={`order-2 lg:order-1 lg:col-span-2 ${(isMobile && activeTab !== 'cardapio') ? 'hidden' : 'block'}`}>
            <div className="bg-gray-800/50 rounded-xl p-3 sm:p-4 border border-gray-600/30">
              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Buscar produto por nome ou código..."
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && busca.trim() && produtoDestaque) {
                        abrirProdutoModal(produtoDestaque);
                        setBusca('');
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault(); navegarBusca('down');
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault(); navegarBusca('up');
                      }
                    }}
                    className="w-full pl-9 pr-8 py-2.5 bg-gray-700/60 border border-gray-600/40 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/50 text-sm transition-all"
                    style={{ fontSize: '16px', color: '#fff' }}
                    autoComplete="off"
                    autoFocus
                    id="busca-produtos"
                  />
                  {busca && (
                    <button onClick={() => setBusca('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="flex rounded-xl overflow-hidden border border-gray-600/40">
                  <button
                    onClick={() => setVisualizacao('grade')}
                    className={`px-3 py-2.5 transition-colors ${visualizacao === 'grade' ? 'bg-orange-500 text-white' : 'bg-gray-700/60 text-gray-400 hover:text-white'}`}
                    title="Grade"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setVisualizacao('lista')}
                    className={`px-3 py-2.5 transition-colors ${visualizacao === 'lista' ? 'bg-orange-500 text-white' : 'bg-gray-700/60 text-gray-400 hover:text-white'}`}
                    title="Lista"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex gap-2 overflow-x-auto pb-2 mb-3 scrollbar-none -mx-1 px-1">
                <button
                  onClick={() => setCategoriaFiltro('')}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all touch-manipulation ${
                    categoriaFiltro === '' ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'bg-gray-700/60 text-gray-400 hover:text-white hover:bg-gray-600/60'
                  }`}
                >
                  Todos
                </button>
                {categorias.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoriaFiltro(cat.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all touch-manipulation ${
                      categoriaFiltro === cat.id ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'bg-gray-700/60 text-gray-400 hover:text-white hover:bg-gray-600/60'
                    }`}
                  >
                    {cat.nome}
                  </button>
                ))}
              </div>

              {buscaDebounced && produtosFiltrados.length > 0 && (
                <div className="mb-4">
                  <SearchNavigation
                    currentIndex={produtoDestaqueIndex}
                    totalResults={produtosFiltrados.length}
                    onNavigate={navegarBusca}
                    onSelect={() => {
                      if (produtoDestaque) {
                        abrirProdutoModal(produtoDestaque);
                      }
                    }}
                    visible={buscaDebounced.length > 0}
                  />
                </div>
              )}

              <div role="region" aria-labelledby="produtos-heading">
                {produtosFiltrados.length > 50 ? (
                  <VirtualizedProductList
                    produtos={produtosFiltrados}
                    produtoDestaqueIndex={produtoDestaqueIndex}
                    buscaDebounced={buscaDebounced}
                    onSelect={abrirProdutoModal}
                    onSwipeLeft={handleSwipeLeft}
                    onSwipeRight={handleSwipeRight}
                    swipeEnabled={isMobile && !showProdutoModal}
                    visualizacao={visualizacao}
                    deviceType={deviceType}
                  />
                ) : (
                  <div className={`grid gap-2 sm:gap-3 ${modoCompacto && isMobile
                    ? 'grid-cols-1 gap-2'
                    : visualizacao === 'grade'
                      ? deviceType === 'mobile'
                        ? 'grid-cols-1'
                        : deviceType === 'ipad'
                          ? 'grid-cols-2'
                          : 'grid-cols-2 xl:grid-cols-3'
                      : 'grid-cols-1'
                    }`}>
                    {produtosFiltrados.map((produto, index) => {
                      const isDestaque = index === produtoDestaqueIndex && buscaDebounced.length > 0;

                      if (modoCompacto && isMobile) {
                        return (
                          <ProductCardCompact
                            key={produto.id}
                            produto={produto}
                            isDestaque={isDestaque}
                            onSelect={() => {
                              if (produto.venda_por_peso) {
                                setQuantidade(1);
                              }
                              abrirProdutoModal(produto);
                            }}
                            onSwipeLeft={() => handleSwipeLeft(produto)}
                            onSwipeRight={() => handleSwipeRight(produto)}
                            swipeEnabled={isMobile && !showProdutoModal}
                          />
                        );
                      }

                      return (
                        <ProductCard
                          key={produto.id}
                          produto={produto}
                          isDestaque={isDestaque}
                          onSelect={() => abrirProdutoModal(produto)}
                          onSwipeLeft={() => handleSwipeLeft(produto)}
                          onSwipeRight={() => handleSwipeRight(produto)}
                          swipeEnabled={isMobile && !showProdutoModal}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {produtosFiltrados.length === 0 && (
                <div className="text-center py-12" role="status" aria-live="polite">
                  <Coffee className="h-16 w-16 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                  <p className="text-gray-400">Nenhum produto encontrado</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showProdutoModal && produtoSelecionado && (
        <ProductModal
          deviceType={deviceType}
          isOpen={showProdutoModal}
          onClose={() => setShowProdutoModal(false)}
          produto={produtoSelecionado}
          quantidade={quantidade}
          observacoes={observacoes}
          peso={peso}
          onPesoChange={setPeso}
          onQuantidadeChange={setQuantidade}
          onObservacoesChange={setObservacoes}
          onAdicionar={adicionarProduto}
          onCancelar={() => {
            setShowProdutoModal(false);
            setProdutoSelecionado(null);
            setQuantidade(1);
            setObservacoes('');
            setPersonalizacoes([]);
          }}
        />
      )}

      {/* Modal de Reenviar/Reimprimir Itens para Cozinha */}
      {showReenviarModal && comanda && comanda.itens && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200 border border-gray-100 dark:border-gray-700/60">
            <div className="p-6">
              <div className="text-center mb-4">
                <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Printer className="w-7 h-7 text-blue-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Reenviar para Cozinha
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                  Selecione os itens para imprimir novamente na cozinha.
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-2.5 max-h-56 overflow-y-auto border border-gray-100 dark:border-gray-700/60 mb-5">
                {(() => {
                  const itensCozinha = comanda.itens.filter(item => item.precisa_cozinha);
                  if (itensCozinha.length === 0) {
                    return (
                      <p className="text-center text-sm py-4 text-gray-400 dark:text-gray-500">
                        Nenhum item desta comanda necessita de preparo de cozinha.
                      </p>
                    );
                  }
                  return (
                    <div className="divide-y divide-gray-200/60 dark:divide-gray-700/40">
                      {itensCozinha.map(item => {
                        const isSelected = itensReenviarIds.includes(item.id);
                        return (
                          <div 
                            key={item.id} 
                            onClick={() => {
                              hapticFeedback('light');
                              setItensReenviarIds(prev => 
                                isSelected 
                                  ? prev.filter(id => id !== item.id) 
                                  : [...prev, item.id]
                              );
                            }}
                            className="flex items-center gap-3 py-3 px-1.5 hover:bg-gray-100 dark:hover:bg-gray-800/40 rounded-xl cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              className="h-5 w-5 rounded-md border-gray-300 dark:border-gray-600 text-blue-500 focus:ring-blue-500"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                                {item.quantidade}x {item.produto_nome}
                              </p>
                              {item.observacoes && (
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                                  Obs: {item.observacoes}
                                </p>
                              )}
                            </div>
                            {item.enviado_cozinha ? (
                              <span className="text-[10px] bg-green-500/10 text-green-500 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                                Já enviado
                              </span>
                            ) : (
                              <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
                                Pendente
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={reenviarParaCozinha}
                  disabled={reenviando || itensReenviarIds.length === 0}
                  className="w-full py-3.5 rounded-2xl font-bold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-40 disabled:hover:bg-blue-500 shadow-lg shadow-blue-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  {reenviando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Reenviando...</span>
                    </>
                  ) : (
                    <span>Reenviar {itensReenviarIds.length > 0 ? `(${itensReenviarIds.length})` : ''}</span>
                  )}
                </button>
                <button
                  onClick={() => setShowReenviarModal(false)}
                  disabled={reenviando}
                  className="w-full py-3 rounded-2xl font-bold text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-white transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMobile && comanda && (
        <ComandaBottomBar
          total={Number(comanda.total) || 0}
          itensPendentesCozinha={itensPendentesCozinha}
          onEnviarCozinha={enviarParaCozinha}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          itensCount={comanda?.itens?.length || 0}
          isOwner={true}
        />
      )}
      
      {showWeightModal && produtoSelecionado && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ChefHat className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                Informe o Peso
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                {produtoSelecionado.nome}
              </p>

              <div className="relative mb-6">
                <input
                  type="tel"
                  inputMode="numeric"
                  autoFocus
                  placeholder="0,000"
                  className="w-full bg-gray-50 dark:bg-gray-900/50 border-2 border-orange-500/20 focus:border-orange-500 rounded-2xl py-4 px-6 text-3xl font-black text-center text-orange-500 focus:outline-none transition-all"
                  onChange={(e) => {
                    const apenasNumeros = e.target.value.replace(/\D/g, '');
                    if (!apenasNumeros) {
                      e.target.value = '';
                      return;
                    }
                    const valorComCasas = (parseInt(apenasNumeros, 10) / 1000).toFixed(3);
                    e.target.value = valorComCasas.replace('.', ',');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const input = e.target as HTMLInputElement;
                      const val = parseFloat(input.value.replace(/\./g, '').replace(',', '.'));
                      if (!isNaN(val) && val > 0) confirmarPeso(val);
                    }
                  }}
                  id="weight-input"
                />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 font-bold text-gray-400">KG</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowWeightModal(false)}
                  className="py-4 rounded-2xl font-bold text-gray-500 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    const input = document.getElementById('weight-input') as HTMLInputElement;
                    const val = parseFloat(input.value.replace(/\./g, '').replace(',', '.'));
                    if (!isNaN(val) && val > 0) {
                      confirmarPeso(val);
                    } else {
                      toast.error("Informe um peso válido");
                    }
                  }}
                  className="py-4 rounded-2xl font-bold text-white bg-orange-500 hover:bg-orange-600 shadow-lg shadow-orange-500/30 transition-all active:scale-95"
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModalCozinha && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ChefHat className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Enviar para Cozinha
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
                Deseja imprimir os pedidos na cozinha?
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-3 mb-4 max-h-32 overflow-y-auto text-left border border-gray-100 dark:border-gray-700">
                {itensCozinhaDetalhes.map(item => (
                  <div key={item.id} className="text-sm text-gray-700 dark:text-gray-300 flex justify-between py-1.5 border-b border-gray-200 dark:border-gray-700 last:border-0">
                    <span className="truncate font-medium">{item.quantidade}x {item.produto_nome}</span>
                  </div>
                ))}
              </div>

              {msgCozinhaAux && (
                <p className="text-xs text-orange-500 dark:text-orange-400 mb-6 font-semibold">
                  * {msgCozinhaAux}
                </p>
              )}

              <div className="grid grid-cols-2 gap-3 mb-3">
                <button
                  onClick={() => confirmarEnvioCozinha(false)}
                  className="py-4 rounded-2xl font-bold text-gray-700 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 transition-colors"
                >
                  Não
                </button>
                <button
                  onClick={() => confirmarEnvioCozinha(true)}
                  className="py-4 rounded-2xl font-bold text-white bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30 transition-all active:scale-95"
                >
                  Sim
                </button>
              </div>
              <button
                onClick={() => setShowModalCozinha(false)}
                className="w-full py-3 rounded-2xl font-bold text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showDefinirCodigoModal && produtoPendenteCodigo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Grid3X3 className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                Atalho / Código Rápido
              </h3>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                Este produto não possui código de atalho. <br />
                Deseja definir um código rápido para o item <span className="font-bold text-orange-500">{produtoPendenteCodigo.nome}</span>?
              </p>

              <div className="relative mb-6">
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  placeholder="Ex: 1, 12, 45"
                  value={codigoParaDefinir}
                  onChange={(e) => setCodigoParaDefinir(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-900/50 border-2 border-orange-500/20 focus:border-orange-500 rounded-2xl py-4 px-6 text-2xl font-black text-center text-orange-500 focus:outline-none transition-all"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      confirmarDefinicaoCodigo();
                    }
                  }}
                  id="codigo-definir-input"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={pularDefinicaoCodigo}
                  className="py-4 rounded-2xl font-bold text-gray-500 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  Pular (Sem Código)
                </button>
                <button
                  onClick={confirmarDefinicaoCodigo}
                  className="py-4 rounded-2xl font-bold text-white bg-orange-500 hover:bg-orange-600 shadow-lg shadow-orange-500/30 transition-all active:scale-95"
                >
                  Salvar e Lançar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default GarcomComandaPage;